import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import type { TFunction } from 'i18next';
import type { TppEventSummary } from '../../../services/transitPassPool.service';
import type { TppHistoryColumnId } from '../../../utils/tppHistoryTableLayout';

/** 未返却の貸出は「貸出」、返却済みは「利用」と区別する */
export function displayTppEventKind(
  ev: Pick<TppEventSummary, 'eventKind' | 'checkoutStatus'>,
  t: TFunction
): string {
  if (ev.eventKind === 'charge') {
    return t('tpp.event_kind.charge');
  }
  if (ev.checkoutStatus === 1) {
    return t('tpp.event_kind.checkout_open');
  }
  return t('tpp.event_kind.usage');
}

/**
 * 証跡の閲覧／後付け UI を出す行か。
 * 未返却かつ証跡なしは返却モーダルに任せ、返却済み（または既存証跡）は履歴から付ける。
 */
export function tppEventNeedsProofUi(
  ev: Pick<TppEventSummary, 'eventKind' | 'hasProof' | 'checkoutStatus'>
): boolean {
  if (ev.eventKind !== 'usage') return false;
  if (ev.hasProof) return true;
  return ev.checkoutStatus !== 1;
}

export function formatHistoryDate(iso: string | null | undefined, useJa: boolean): string {
  if (!iso) return '—';
  const day = iso.slice(0, 10);
  if (!useJa) return day;
  try {
    return format(parseISO(day), 'yyyy年M月d日', { locale: ja });
  } catch {
    return day;
  }
}

export function defaultSortDir(col: TppHistoryColumnId): 'asc' | 'desc' {
  if (col === 'outDate' || col === 'inDate' || col === 'id' || col === 'amount' || col === 'checkoutStatus') {
    return 'desc';
  }
  return 'asc';
}

export function formatHistoryAmount(
  ev: TppEventSummary,
  prefix: string,
  suffix: string,
  formatAbs: (n: number) => string = (n) => Math.abs(n).toLocaleString()
): { text: string; className: string } {
  const wrap = (signed: string) => `${prefix}${signed}${suffix}`;
  if (ev.amountNumber == null) {
    return { text: '', className: 'tabular-nums' };
  }
  if (ev.eventKind === 'charge') {
    if (Math.abs(ev.amountNumber) === 0) {
      return { text: '', className: 'tabular-nums' };
    }
    return {
      text: wrap(`+${formatAbs(ev.amountNumber)}`),
      className: 'tabular-nums text-success',
    };
  }
  if (ev.amountNumber === 0) {
    return { text: wrap(formatAbs(0)), className: 'tabular-nums text-muted' };
  }
  // 利用額は支出。チャージの + と対になるよう先頭に − を付ける
  return {
    text: wrap(`-${formatAbs(ev.amountNumber)}`),
    className: 'tabular-nums text-danger',
  };
}
